// ADR-132: Job Position Mappings (MISC → Ingestion).
//
// The Americas and PowerBI employee syncs translate a free-text source job
// title into a Radar position through these rows. They used to be compiled into
// radar-ingestion, so every new title meant a rebuild and a deploy; now the sync
// reads this list at the start of each run.
//
// One list per provider (/ingestion/job-position-mappings/:provider; the
// overview with one card per provider is Overview.jsx): the two sources use
// different title vocabularies. Matching (exact → most specific pattern → catch-all → automatic
// fuzzy matching) happens in radar-ingestion (ADR-015); this page only edits the
// list, and every change is versioned and recorded in the history.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Navigate, useLocation, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import {
  FaArrowLeft, FaFileExport, FaFileImport, FaPlus, FaHistory, FaSearch,
} from 'react-icons/fa';
import {
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Container, Header, HeaderLeft, Title, Content, MainSection, BackButton, HeaderButton,
} from '../../common/styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import ConfirmationModal from '../../common/ConfirmationModal';
import Can from '../../common/Can';
import { useAlert } from '../../../contexts/AlertContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import {
  INGESTION_MAPPING_OUTCOMES,
  JOB_POSITION_MAPPINGS_PATH,
  findProviderBySlug,
  INGESTION_MAPPING_OUTCOME_LABELS,
  INGESTION_PATTERN_MATCH_TYPES,
  INGESTION_PATTERN_MATCH_TYPE_LABELS,
  INGESTION_PATTERN_MATCH_TYPE_COLORS,
} from '../../../constants/ingestion';
import { ingestionConfigurationApi } from '../../../services/api/ingestionConfigurationApi';
import { filterMappings, buildExportFileName } from '../../../utils/ingestionMappings';
import MappingDialog from './MappingDialog';
import ImportDialog from './ImportDialog';
import HistoryPanel from './HistoryPanel';
import UnmappedTitlesPanel from './UnmappedTitlesPanel';
import useUnmappedTitles from './useUnmappedTitles';
import UnmappedRunStrip from './UnmappedRunStrip';
import { useAbility } from '../../../contexts/AbilityContext';
import { INGESTION_SYNC_RUNS_PATH } from '../../../constants/ingestionSyncRuns';
import {
  Toolbar, SetInfo, PatternCell, MutedText, HeaderRight, TabContainer, TabButton,
} from './styles';

const ALL = 'ALL';

// Match chips (filter + rows): pastel background, darker text — the report chip style.
const matchChipSx = (matchType) => {
  const colors = INGESTION_PATTERN_MATCH_TYPE_COLORS[matchType] || { bg: '#F3F4F6', color: '#6B7280' };
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 500,
    border: 'none',
    '& .MuiChip-label': { color: colors.color },
  };
};

// Tabs (same pattern as Edit Individual); deep-linkable with ?tab=.
const TABS = Object.freeze({ MAPPINGS: 'mappings', UNMAPPED: 'unmapped', HISTORY: 'history' });
const TAB_LABELS = {
  [TABS.MAPPINGS]: 'Mappings',
  [TABS.UNMAPPED]: 'Unmapped in last run',
  [TABS.HISTORY]: 'History',
};

const JobPositionMappings = () => {
  const { provider: providerSlug } = useParams();
  const providerOption = findProviderBySlug(providerSlug);
  if (!providerOption) {
    return <Navigate to={JOB_POSITION_MAPPINGS_PATH} replace />;
  }
  return <JobPositionMappingList key={providerOption.value} providerOption={providerOption} />;
};

const JobPositionMappingList = ({ providerOption }) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const provider = providerOption.value;
  const [set, setSet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState(ALL);
  const [matchTypeFilter, setMatchTypeFilter] = useState([]);
  const [dialog, setDialog] = useState({ open: false, mapping: null, initialPattern: '' });
  const [toDelete, setToDelete] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab = Object.values(TABS).includes(requestedTab) ? requestedTab : TABS.MAPPINGS;
  const selectTab = (tab) => setSearchParams(tab === TABS.MAPPINGS ? {} : { tab }, { replace: true });
  const location = useLocation();
  const ability = useAbility();
  const canOpenRun = ability.can(ACTIONS.READ, SUBJECTS.INGESTION_SYNC_RUN);
  // ADR-133 D11: shared by the Unmapped tab and its header strip; loaded once the tab is opened.
  const unmapped = useUnmappedTitles(provider, set?.version, activeTab === TABS.UNMAPPED);

  const load = useCallback(async (targetProvider) => {
    try {
      setIsLoading(true);
      setSet(await ingestionConfigurationApi.getJobPositionMappings(targetProvider));
    } catch (error) {
      setSet(null);
      showAlert('Failed to load job position mappings', 'error', error);
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    load(provider);
  }, [load, provider]);

  const rows = useMemo(
    () => filterMappings(set?.mappings || [], { search, outcome: outcomeFilter, matchType: matchTypeFilter }),
    [set, search, outcomeFilter, matchTypeFilter],
  );

  const announceWarnings = (warnings = []) => {
    warnings.forEach((warning) => showAlert(warning, 'warning'));
  };

  const handleSave = async (values) => {
    const result = dialog.mapping
      ? await ingestionConfigurationApi.updateJobPositionMapping(dialog.mapping.id, values)
      : await ingestionConfigurationApi.createJobPositionMapping({ provider, ...values });
    setDialog({ open: false, mapping: null, initialPattern: '' });
    showAlert(`Saved. ${providerOption.label} mappings are now at version ${result.version}; the next sync run uses them.`, 'success');
    announceWarnings(result.warnings);
    await load(provider);
  };

  const handleDelete = async () => {
    const mapping = toDelete;
    setToDelete(null);
    try {
      const result = await ingestionConfigurationApi.deleteJobPositionMapping(mapping.id);
      showAlert(`Removed “${mapping.sourcePattern}”. Version ${result.version}.`, 'success');
      await load(provider);
    } catch (error) {
      showAlert('Failed to remove the mapping', 'error', error);
    }
  };

  const handleImportApply = async ({ items, mode }) => {
    const result = await ingestionConfigurationApi.importJobPositionMappings({ provider, items, mode, dryRun: false });
    setImportOpen(false);
    showAlert(
      result.applied
        ? `Imported: ${result.summary.create} added, ${result.summary.update} changed, ${result.summary.delete} removed. Version ${result.version}.`
        : 'Nothing to import: the list already matches the file.',
      'success',
    );
    await load(provider);
  };

  const handleExport = async () => {
    try {
      const data = await ingestionConfigurationApi.exportJobPositionMappings(provider);
      const blob = new Blob([`${JSON.stringify(data, null, 2)}\n`], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildExportFileName(provider, data.version);
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      showAlert('Failed to export mappings', 'error', error);
    }
  };

  const noPositionCount = (set?.mappings || []).filter((m) => m.outcome === INGESTION_MAPPING_OUTCOMES.NO_POSITION).length;
  const hasCatchAll = (set?.mappings || []).some((m) => m.matchType === INGESTION_PATTERN_MATCH_TYPES.CATCH_ALL);

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(JOB_POSITION_MAPPINGS_PATH)}>
            <FaArrowLeft /> Back to Job Position Mappings
          </BackButton>
          <Title>{`${providerOption.label} Job Position Mappings`}</Title>
        </HeaderLeft>
        {activeTab === TABS.UNMAPPED && (
          <HeaderRight>
            <UnmappedRunStrip unmapped={unmapped} />
            {canOpenRun && unmapped.data?.runId && (
              <HeaderButton
                onClick={() => navigate(`${INGESTION_SYNC_RUNS_PATH}/${unmapped.data.runId}`, {
                  // The run detail's Back button returns here (ADR-133).
                  state: {
                    returnTo: location.pathname + location.search,
                    returnLabel: `${providerOption.label} Job Position Mappings`,
                  },
                })}
              >
                <FaHistory /> Open run
              </HeaderButton>
            )}
          </HeaderRight>
        )}
        {activeTab === TABS.MAPPINGS && (
          <HeaderRight>
            <HeaderButton onClick={handleExport} disabled={!set}>
              <FaFileExport /> Export
            </HeaderButton>
            <Can I={ACTIONS.MANAGE} a={SUBJECTS.INGESTION_CONFIGURATION}>
              <HeaderButton onClick={() => setImportOpen(true)}>
                <FaFileImport /> Import
              </HeaderButton>
              <HeaderButton onClick={() => setDialog({ open: true, mapping: null })}>
                <FaPlus /> Add Mapping
              </HeaderButton>
            </Can>
          </HeaderRight>
        )}
      </Header>

      <TabContainer>
        {Object.values(TABS).map((tab) => (
          <TabButton key={tab} type="button" data-active={activeTab === tab} onClick={() => selectTab(tab)}>
            {TAB_LABELS[tab]}
          </TabButton>
        ))}
      </TabContainer>

      <Content>
        <MainSection>
          {activeTab === TABS.MAPPINGS && (
            <>
            <SetInfo>
              <span>Read by the <strong>{providerOption.syncHint}</strong> at the start of each run.</span>
              {set && (
                <>
                  <span>Version <strong>{set.version}</strong></span>
                  <span><strong>{set.count}</strong> mappings ({noPositionCount} with no position)</span>
                  {set.updatedAt && set.version > 0 && (
                    <span>Last changed by {set.updatedBy} on {new Date(set.updatedAt).toLocaleString()}</span>
                  )}
                </>
              )}
            </SetInfo>

            {hasCatchAll && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                This list has a catch-all (*): every title without a more specific mapping resolves through it,
                so nothing falls through to automatic position matching.
              </Alert>
            )}

            <Toolbar>
              <TextField
                placeholder="Search title, position or notes..."
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 300 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaSearch style={{ color: '#6B7280' }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel id="jpm-result-label">Result</InputLabel>
                <Select
                  labelId="jpm-result-label"
                  label="Result"
                  value={outcomeFilter}
                  onChange={(e) => setOutcomeFilter(e.target.value)}
                >
                  <MenuItem value={ALL}>All</MenuItem>
                  {Object.entries(INGESTION_MAPPING_OUTCOME_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Same as the Discrepancy Audit status filter: coloured chips in the input and the menu. */}
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="jpm-match-label" shrink>Match</InputLabel>
                <Select
                  labelId="jpm-match-label"
                  multiple
                  displayEmpty
                  value={matchTypeFilter}
                  onChange={(e) => {
                    const { value } = e.target;
                    setMatchTypeFilter(typeof value === 'string' ? value.split(',') : value);
                  }}
                  input={<OutlinedInput label="Match" notched />}
                  renderValue={(selected) => (selected.length === 0 ? 'All' : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} size="small" label={INGESTION_PATTERN_MATCH_TYPE_LABELS[value] || value} sx={matchChipSx(value)} />
                      ))}
                    </Box>
                  ))}
                >
                  {Object.entries(INGESTION_PATTERN_MATCH_TYPE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      <Chip size="small" label={label} sx={matchChipSx(value)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Toolbar>

            {isLoading && <LoadingSpinner />}

            {!isLoading && set && set.count === 0 && (
              <EmptyState
                title={`No ${providerOption.label} mappings yet`}
                description="Add mappings one by one, or import the seed file exported from radar-ingestion (src/config/seed)."
              />
            )}

            {!isLoading && set && set.count > 0 && (
              <>
                <ResultsCount count={rows.length} singular="mapping" plural="mappings" />
                {/* No outer frame: only the row separators remain. */}
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{providerOption.sourceLabel}</TableCell>
                        <TableCell>Match</TableCell>
                        <TableCell>Result</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell>Last changed</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((mapping) => (
                        <TableRow key={mapping.id} hover>
                          <TableCell><PatternCell>{mapping.sourcePattern}</PatternCell></TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={INGESTION_PATTERN_MATCH_TYPE_LABELS[mapping.matchType] || mapping.matchType}
                              sx={matchChipSx(mapping.matchType)}
                            />
                          </TableCell>
                          <TableCell>
                            {mapping.outcome === INGESTION_MAPPING_OUTCOMES.NO_POSITION ? (
                              <Tooltip title="Synced without a position; never sent to automatic position matching.">
                                <Chip size="small" label="No position" />
                              </Tooltip>
                            ) : (
                              mapping.position?.label
                            )}
                          </TableCell>
                          <TableCell>{mapping.notes ? mapping.notes : <MutedText>—</MutedText>}</TableCell>
                          <TableCell>
                            <MutedText>
                              {mapping.updatedBy} · {new Date(mapping.updatedAt).toLocaleDateString()}
                            </MutedText>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Can I={ACTIONS.MANAGE} a={SUBJECTS.INGESTION_CONFIGURATION}>
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => setDialog({ open: true, mapping })}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton size="small" onClick={() => setToDelete(mapping)}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Can>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            </>
          )}

          {activeTab === TABS.UNMAPPED && (
            <UnmappedTitlesPanel
              unmapped={unmapped}
              providerLabel={providerOption.label}
              onShowMapping={(title) => {
                setSearch(title);
                selectTab(TABS.MAPPINGS);
              }}
              onMapTitle={(title) => setDialog({ open: true, mapping: null, initialPattern: title })}
            />
          )}

          {activeTab === TABS.HISTORY && <HistoryPanel provider={provider} refreshKey={set?.version} />}
        </MainSection>
      </Content>

      <MappingDialog
        open={dialog.open}
        mapping={dialog.mapping}
        initialPattern={dialog.initialPattern}
        providerLabel={providerOption.label}
        sourceLabel={providerOption.sourceLabel}
        onClose={() => setDialog({ open: false, mapping: null, initialPattern: '' })}
        onSave={handleSave}
      />

      <ImportDialog
        open={importOpen}
        provider={provider}
        providerLabel={providerOption.label}
        onClose={() => setImportOpen(false)}
        onPreview={({ items, mode }) => ingestionConfigurationApi.importJobPositionMappings({ provider, items, mode, dryRun: true })}
        onApply={handleImportApply}
      />

      <ConfirmationModal
        isOpen={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
        title="Remove mapping"
        message={toDelete
          ? `Remove “${toDelete.sourcePattern}”? From the next ${providerOption.label} sync run, matching titles fall through to the next most specific mapping, or to automatic position matching.`
          : ''}
        actionButtonText="Remove"
      />
    </Container>
  );
};

export default JobPositionMappings;
